import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG, SettingsService } from 'src/app/services/settings.service';
import {
  ITaxRegisterLogger,
  MonitoringService,
} from 'src/app/services/monitoring.service';
import { TenantAuthService } from 'src/app/services/tenant-auth.service';
import { TaxRegisterContextService } from './tax-register-context.service';
import { readDocumentIdentity } from './fake-prro';
import { FakePrroStore, RealDocumentDelivery } from './fake-prro-store.service';

// Sends the fiscal documents the registers save to the backend outbox. Fake documents (fakePrro:
// made-up numbers) never go there; their real versions are delivered once the tax server has
// registered them — into the outbox, and onto their receipt.
@Injectable({ providedIn: 'root' })
export class TaxRegisterWorker implements OnDestroy {
  private _taxLogger: ITaxRegisterLogger;
  private _stopped = false;
  private _delayTimer: ReturnType<typeof setTimeout> | undefined;
  private _resumeDelay: (() => void) | undefined;

  constructor(
    private _settings: SettingsService,
    private _http: HttpClient,
    private _context: TaxRegisterContextService,
    private _fakePrro: FakePrroStore,
    monitoring: MonitoringService,
    private _zone: NgZone,
    private _tenantAuth: TenantAuthService,
  ) {
    this._taxLogger = monitoring.getTaxLogger();
    // Keep storage polling outside UI checks. HTTP re-enters Angular because
    // the request interceptors publish network and authentication changes.
    this._zone.runOutsideAngular(() => void this._run());
  }

  ngOnDestroy(): void {
    this._stopped = true;
    clearTimeout(this._delayTimer);
    this._resumeDelay?.();
  }

  private async _run(): Promise<void> {
    while (!this._stopped) {
      // Logged out (the login page too): the documents are in the company storage, which
      // needs the tenant; it alerts without one.
      if (!this._tenantAuth.currentTenant) {
        await this._delay(3000);
        continue;
      }
      try {
        const batchSent = await this._sendBatch();
        if (this._stopped) {
          return;
        }
        // Give receipt updates a turn even while staged documents keep arriving.
        const deliverySent = await this._sendDelivery();
        // Yield between batches too: an await on resolved storage promises alone
        // does not give the browser a chance to paint or handle input.
        await this._delay(batchSent || deliverySent ? 50 : 3000);
      } catch (error) {
        console.error('Failed to send fiscal documents to the backend.', error);
        await this._delay(10000);
      }
    }
  }

  private async _sendBatch(): Promise<boolean> {
    const xmlDocuments = await this._context.getStagedDocumentBatch();
    if (!xmlDocuments.length) {
      return false;
    }

    const fakeDocuments = await this._fakePrro.getDocuments();
    const documents = xmlDocuments.filter(
      (xml) => !fakeDocuments[readDocumentIdentity(xml).uid],
    );
    if (documents.length) {
      const apiEndpoint = await this._getApiEndpoint();
      await this._zone.run(() =>
        this._http
          .post(`${apiEndpoint}/fiscal-documents/outbox`, documents)
          .toPromise(),
      );
    }
    if (documents.length < xmlDocuments.length) {
      this._taxLogger.logTrace('fakePrro: fake documents kept out of the outbox', {
        count: xmlDocuments.length - documents.length,
      });
    }

    await this._context.acknowledgeStagedDocuments(xmlDocuments);
    return true;
  }

  // One real document per call: into the outbox, then onto its receipt. A receipt the backend
  // cannot find (404) or a request it refuses (400) ends the delivery; any other failure moves it
  // to the end of the queue, to be tried again.
  private async _sendDelivery(): Promise<boolean> {
    const [delivery] = await this._fakePrro.getDeliveries();
    if (!delivery) {
      return false;
    }

    const apiEndpoint = await this._getApiEndpoint();
    const identity = readDocumentIdentity(delivery.xml);
    try {
      if (!delivery.outboxSent) {
        await this._zone.run(() =>
          this._http
            .post(`${apiEndpoint}/fiscal-documents/outbox`, [delivery.xml])
            .toPromise(),
        );
        delivery.outboxSent = true;
        await this._fakePrro.updateDelivery(delivery);
        this._taxLogger.logTrace('fakePrro: real document sent to the outbox', {
          fiscalNumber: identity.fiscalNumber,
          taxRegister: identity.cashRegisterNum,
        });
      }
      if (delivery.receipt) {
        await this._zone.run(() =>
          this._http
            .put(`${apiEndpoint}/fiscal-documents/receipt`, delivery.receipt)
            .toPromise(),
        );
        this._taxLogger.logTrace('fakePrro: receipt updated with the real document', {
          receiptUuid: delivery.receipt.receiptUuid,
          fiscalNumber: delivery.receipt.fiscalNumber,
          newFiscalNumber: delivery.receipt.newFiscalNumber,
          taxRegister: delivery.receipt.taxRegisterFiscalNumber,
        });
      }
      await this._fakePrro.removeDelivery(delivery.id);
    } catch (error) {
      await this._failDelivery(delivery, error);
    }
    return true;
  }

  private async _failDelivery(
    delivery: RealDocumentDelivery,
    error: { status?: number } | unknown,
  ): Promise<void> {
    const status = (error as { status?: number })?.status;
    const final = status === 400 || status === 404;
    // A dropped delivery is an error; one tried again, a warning.
    this._taxLogger.logException(error, final ? 3 : 2, {
      operation: 'fakePrroDelivery',
      point: delivery.outboxSent ? 'receipt' : 'outbox',
      taxRegister:
        delivery.receipt?.taxRegisterFiscalNumber ||
        readDocumentIdentity(delivery.xml).cashRegisterNum,
      status,
      attempts: delivery.attempts + 1,
      receiptUuid: delivery.receipt?.receiptUuid,
      fiscalNumber: delivery.receipt?.fiscalNumber,
      newFiscalNumber: delivery.receipt?.newFiscalNumber,
      dropped: final,
    });
    if (final) {
      await this._fakePrro.removeDelivery(delivery.id);
      return;
    }
    delivery.attempts++;
    await this._fakePrro.updateDelivery(delivery, true);
    throw error;
  }

  private async _getApiEndpoint(): Promise<string> {
    const configuredEndpoint = await this._settings._getValue<string>(
      APP_CONFIG.POS_API_ENDPOINT,
    );
    return configuredEndpoint.replace(/\/+$/, '');
  }

  private _delay(milliseconds: number): Promise<void> {
    if (this._stopped) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this._resumeDelay = () => {
        this._delayTimer = undefined;
        this._resumeDelay = undefined;
        resolve();
      };
      this._delayTimer = setTimeout(this._resumeDelay, milliseconds);
    });
  }
}
